import React from "react";
import { Alert, AlertTitle, Box, Container, Typography } from "@mui/material";
import { Form, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { LocalFormSchema, zLocalFormSchema } from "../logic/applicationUpdateSchema";
import { asOptions, genderMapping, languagePreferenceMapping } from "../utils/option-data";
import EnumBackedAutocomplete from "../components/EnumBackedAutocomplete";
import OptionalTextField from "../components/OptionalTextField";
import { TestingInfoBlock, TestingInfoBlockFormErrors } from "../components/TestingInfo";
import { applicationRefinement } from "../logic/dbsRules";
import { ensureUppercase, trimTextFields } from "../utils/forms";
import { useRedirectToGDPRConsentIfRequired } from "./ApplicationGDPRConsent";
import FormButtons from "../components/FormButtons";
import TestingData from "../components/TestingData";
import { useActionPath } from "./ApplicationUpdate";
import TestApplicationAlert from "../components/TestApplicationAlert";

type LanguageWarningProps = {
  form: ReturnType<typeof useForm<LocalFormSchema>>
} & React.ComponentProps<typeof Alert>

const LanguageWarning: React.FC<LanguageWarningProps> = ({ form, ...alertProps }) => {
  const languagePreference = form.watch('AdditionalApplicantDetails.LanguagePreference')

  if (languagePreference === 'welsh') {
    return (
      <Alert severity="error" {...alertProps}>
        If you would like to submit your application in Welsh, please contact us so that we can provide you with a Welsh language paper application form.
      </Alert>
    )
  }

  return null
}

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  const readonly = getEBulkApplicationByID.data?.status !== 'draft'

  useRedirectToGDPRConsentIfRequired(application_id)
  const actionPath = useActionPath('names')

  // Build the Form
  const form = useForm<LocalFormSchema>({
    resolver: zodResolver(
      zLocalFormSchema
        .superRefine(applicationRefinement('identity'))
    ),
    mode: 'onBlur',
    defaultValues: {
      ...getEBulkApplicationByID.data?.data as any, // NOTE: This is a hack, as I don't understand the typescript error!
      AdditionalApplicantDetails: {
        ...getEBulkApplicationByID.data?.data.AdditionalApplicantDetails ?? {},
        LanguagePreference: getEBulkApplicationByID.data?.data.AdditionalApplicantDetails?.LanguagePreference ?? '',
      },
      Gender: getEBulkApplicationByID.data?.data.Gender ?? '',
      NiNumber: getEBulkApplicationByID.data?.data.NiNumber ?? '',
    }
  })

  // Build the output
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  return <>
    <Helmet>
      <title>{readonly ? 'View' : 'Update'} Identity</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          {readonly ? 'View' : 'Update'} DBS Application
        </Typography>
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
        <Typography component="h2" variant="h5" marginBottom={3}>
          Identity
        </Typography>
        <Typography variant="body1" marginBottom={1}>
          Tell us a little more about who you are.
        </Typography>

        <Form action={actionPath.url} method="post" onBlur={() => {
          trimTextFields(form)
          ensureUppercase(form, 'NiNumber')
        }}>
          {!readonly && (
            <TestingInfoBlock>
              <TestingData form={form} page="identity" />
            </TestingInfoBlock>
          )}
          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Language
          </Typography>
          <EnumBackedAutocomplete
            control={form.control}
            label="Preferred Language"
            name="AdditionalApplicantDetails.LanguagePreference"
            options={asOptions(languagePreferenceMapping)}
            disabled={readonly}
            sx={{ marginTop: 2 }}
          />
          <LanguageWarning form={form} sx={{ marginTop: 2, marginBottom: 1 }} />
          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Gender
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            Please provide the gender you were assigned at birth (written on your birth certificate). The DBS verification process checks the gender provided here against your birth certificate.
          </Typography>
          <Alert severity="info" sx={{ marginBottom: 1 }}>
            {/* TODO: Replace with a link to the page on the RB site */}
            {/* TODO: Check with the DBS what we should here */}
            If you have a Gender Recognition Certificate (GRC) or would like a more sensitive process, please <a href="https://www.gov.uk/guidance/transgender-applications" target="_blank" rel="noreferrer">read this</a> for an alternative application path.
          </Alert>
          <EnumBackedAutocomplete
            control={form.control}
            label="Gender"
            name="Gender"
            options={asOptions(genderMapping)}
            disabled={readonly}
            sx={{ marginTop: 2 }}
          />

          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Contact Phone Number
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            Use the format <code>07712345678</code>.
          </Typography>
          <TestingInfoBlock>
            <Alert severity="info" sx={{ marginTop: 3 }}>
              <AlertTitle>Only shown in Test Mode</AlertTitle>
              Only numbers are allowed. All other characters are filtered out.
            </Alert>
          </TestingInfoBlock>
          <OptionalTextField
            {...textFieldElementCommonProps}
            label="Contact Number (optional)"
            name="AdditionalApplicantDetails.ContactNumber"
            onChangeValueTransformer={value => value.replace(/\D/g, '')}
            disabled={readonly}
          />

          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            National Insurance Number
          </Typography>
          <Typography variant="body1" marginBottom={1}>
            Use the format <code>AA123456B</code>.
          </Typography>
          <TestingInfoBlock>
            <Alert severity="info" sx={{ marginTop: 3 }}>
              <AlertTitle>Only shown in Test Mode</AlertTitle>
              Whitespace around this field will be auto-trimmed.
            </Alert>
          </TestingInfoBlock>
          <TextFieldElement
            {...textFieldElementCommonProps}
            label="National Insurance Number"
            name="NiNumber"
            disabled={readonly}
          />

          <TestingInfoBlockFormErrors form={form} />

          <FormButtons actionPath={actionPath} form={form} readonly={readonly} />
        </Form>
      </Box>
    </Container>
  </>
}

export default Element
